'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '@/lib/validations/auth';
import { getSession, signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import Link from 'next/link';
import Image from 'next/image';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { showToast } from '@/lib/toast';
import { handleApiErrors } from '@/lib/api-error-utils';
import { Loader2 } from 'lucide-react';
import ForgotPassword from './ForgotPassword';
import { signinAction } from '@/app/actions/auth.actions';
import { LogoImage, LogoRegister, LogoSlogan, LogoSmall, LogoText } from '@/components/common';
import CommonNav from '@/components/layouts/CommonNav';

export default function SignIn() {
  const searchParams = useSearchParams();
  let callbackUrl = searchParams.get('callbackUrl') || '/';

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openForgotPasswordDialogue, setOpenForgotPasswordDialogue] = useState(false);
  const router = useRouter();

  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (formData) => {
    try {
      // ('use server');

      // const data = await signinAction({
      //   email: formData.email,
      //   password: formData.password,
      // });

      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/signin`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData }),
      });

      const data = await res.json();

      if (data.statusCode === 200) {
        const user = data?.data?.user;

        if (user?.role === 'admin') {
          callbackUrl = '/admin/users';
        }

        if (!user?.emailVerifiedAt) {
          sessionStorage.setItem('not-verified', true);
          callbackUrl = '/email-verification';
        }

        if (data?.data?.user) {
          const result = await signIn('credentials', {
            user: JSON.stringify(user),
            accessToken: data?.data?.access_token,
            redirect: true, // allow NextAuth to handle cookie + redirect
            callbackUrl: callbackUrl || '/',
          });

          if (result?.error) {
            showToast(
              'Incorrect login credentials. Please verify your credentials and try again.',
              'error'
            );
            return;
          }

          if (!user?.emailVerifiedAt) {
            sessionStorage.setItem('not-verified', true);
          }

          if (user?.role === 'admin') {
            router.push('/admin');
            return;
          } else if (callbackUrl) {
            router.push(callbackUrl);
            return;
          }
          router.push('/chat');
        }
      } else {
        showToast(
          'Incorrect login credentials. Please verify your credentials and try again.',
          'error'
        );
        return;
      }
    } catch (error) {
      showToast('An error occur. Please try again.', 'error');
    }
  };

  const handleGoogleLogin = () => {
    router.push(`${process.env.NEXT_PUBLIC_API_URL}/auth/google`);
  };

  return (
    <div
      className="flex h-full min-h-screen items-center justify-center overflow-auto p-4"
      style={{
        background: 'linear-gradient(180deg, #678CCD 0%, #FFFFFF 100%)',
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm">
        {/* Logo */}
        <div className="mb-2">
          <Link href="/" className={`flex flex-col items-center justify-center`}>
            <div className="flex">
              <LogoImage />
              {/* <LogoText className="text-4xl" />
              <LogoRegister className="text-xl font-semibold" /> */}
            </div>
            <LogoSlogan className="me-4 text-center text-[.8rem]" />
          </Link>
        </div>
        <div className="mb-3">
          <CommonNav />
        </div>

        {/* Heading */}
        <div className="mb-8 text-start">
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Login to your account</h1>
          <p className="text-dark-gray">
            <span className="me-1">Don't have an account?</span>
            <Link
              href={callbackUrl ? `/signup?callbackUrl=${callbackUrl}` : '/signup'}
              className="text-dark-blue font-medium hover:underline"
            >
              Sign Up for FREE
            </Link>
          </p>
        </div>

        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <Label htmlFor="email" className="sr-only">
                    Email
                  </Label>
                  <div className="relative">
                    <Image
                      src="/assets/icons/email.png"
                      width={20}
                      height={20}
                      alt="email"
                      className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform"
                    />
                    <FormControl>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Email "
                        className="h-12 rounded-xl border-gray-200 bg-gray-50 pl-10 text-[#776F69]"
                        {...field}
                        required
                      />
                    </FormControl>
                  </div>
                  <FormMessage className="text-sm text-red-600" />
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <Label htmlFor="password" className="sr-only">
                    Password
                  </Label>
                  <div className="relative">
                    <Image
                      src="/assets/icons/key-icon.png"
                      width={20}
                      height={20}
                      alt="password"
                      className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform"
                    />
                    <FormControl>
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        className="h-12 rounded-xl border-gray-200 bg-gray-50 pr-10 pl-10 text-[#776F69]"
                        {...field}
                      />
                    </FormControl>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 cursor-pointer" />
                      ) : (
                        <Eye className="h-5 w-5 cursor-pointer" />
                      )}
                    </button>
                  </div>
                  <FormMessage className="text-sm text-red-600" />
                </FormItem>
              )}
            />

            {/* Forgot Password */}
            <div className="text-right">
              <Button
                variant="link"
                href="/forgot-password"
                className="text-dark-gray text-sm font-medium hover:text-gray-800 hover:no-underline"
                type="button"
                // disabled
                onClick={() => {
                  setOpenForgotPasswordDialogue(true);
                }}
              >
                Forgot Password?
              </Button>
            </div>

            {/* Submit Button */}
            <div>
              <Button
                variant={'default'}
                type="submit"
                className="bg-dark-blue hover:bg-dark-blue/80 h-12 w-full cursor-pointer rounded-4xl text-lg font-medium text-white"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    Signing in...
                    <Loader2 className="size-7 animate-spin" />
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </div>

            {/* Divider */}
            <div className="relative">
              <Separator className="my-6" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="bg-white px-2 text-sm text-gray-500">OR</span>
              </div>
            </div>

            {/* Social Buttons */}
            <div className="space-y-3">
              <Button
                variant="outline"
                className="bg-muted-brown h-12 w-full cursor-pointer rounded-4xl border-gray-200 font-medium hover:bg-gray-100"
                type="button"
                disabled={form.formState.isSubmitting}
                onClick={handleGoogleLogin}
              >
                <Image
                  src="/assets/icons/google.png"
                  width={20}
                  height={20}
                  alt="Google"
                  className="mr-3"
                />
                Continue with Google
              </Button>

              {/* <Button
                variant="outline"
                className="w-full h-12 bg-muted-brown border-gray-200 rounded-4xl font-medium hover:bg-gray-100"
                type="button"
                disabled={form.formState.isSubmitting}
                // disabled
              >
                <Image
                  src="/assets/icons/apple.png"
                  width={20}
                  height={20}
                  alt="Apple"
                  className="mr-3"

                />
                Continue with Apple
              </Button> */}

              <div>
                <p className="text-center text-xs text-gray-500 sm:text-sm">
                  By continuing, you agree to our
                </p>
                <p className="text-center text-xs text-gray-500 sm:text-sm">
                  <Link href="/pages/terms-of-use" className="text-black">
                    Term of Use
                  </Link>{' '}
                  and{' '}
                  <Link href="/pages/privacy-policy" target="_blank" className="text-black">
                    Privacy Policy.
                  </Link>
                </p>
              </div>
            </div>
          </form>
        </Form>
      </div>

      {openForgotPasswordDialogue && (
        <ForgotPassword
          openForgotPasswordDialogue={openForgotPasswordDialogue}
          setOpenForgotPasswordDialogue={setOpenForgotPasswordDialogue}
        />
      )}
    </div>
  );
}
